import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router';
import { AuthProvider, msalInstance } from './auth/AuthProvider';
import { loginRequest } from './auth/msalConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 15_000,
    },
  },
});

const theme = createTheme({
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: 'ui-monospace, "SF Mono", "Cascadia Code", Menlo, Consolas, monospace',
  primaryColor: 'blue',
  primaryShade: { light: 6 },
  defaultRadius: 'md',
  autoContrast: true,
  cursorType: 'pointer',
  headings: { fontWeight: '600' },
  components: {
    Card: { defaultProps: { withBorder: true, radius: 'md', shadow: 'xs' } },
    Table: { defaultProps: { verticalSpacing: 'sm', highlightOnHover: true } },
    Badge: { defaultProps: { variant: 'light' } },
    Button: { defaultProps: { radius: 'md' } },
    TextInput: { defaultProps: { radius: 'md' } },
    Select: { defaultProps: { radius: 'md' } },
    MultiSelect: { defaultProps: { radius: 'md' } },
  },
});

const el = document.getElementById('root');
if (!el) throw new Error('Root element not found');

function mount() {
  createRoot(el!).render(
    <StrictMode>
      <AuthProvider>
        <MantineProvider theme={theme}>
          <Notifications position="top-right" />
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </MantineProvider>
      </AuthProvider>
    </StrictMode>,
  );
}

// Initialise MSAL, settle any redirect response, then mount. If there's no
// cached account, kick off an interactive login redirect — the app is
// fully gated, there is no anonymous view.
msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((response) => {
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    }
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return msalInstance.loginRedirect(loginRequest);
    }
    if (!msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0]!);
    }
    return undefined;
  })
  .catch((e: unknown) => console.error('[MSAL] init error:', e))
  .finally(mount);
